import { useState } from 'react'
import { Button, Empty, Form, FormInstance, Input, Radio, Select, Switch, Tooltip } from 'antd'
import styled from 'styled-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAlignCenter, faImage, faImages, faTrash } from '@fortawesome/pro-duotone-svg-icons'

import * as CmsImageService from '../../../../services/bb-api/cms/image'

import { txt } from '../../../../locales/i18n'
import { AnimationCollapse } from './animation'
import { getImageSource } from '../layouts/utils'
import { ImageCatalogue } from '../image-catalogue'

export type TextPanelProps = {
	propertyKey: string
	formRef: React.RefObject<FormInstance>
	onSectionChange: (changedValues: any, values: any) => void
}

export const ImageContainerPanel = (props: TextPanelProps) => {
	const [openImageCatalogue, setOpenImageCatalogue] = useState(false)

	const handleSelectImage = (imageName: string) => {
		const form = props.formRef.current
		if (form === null) return

		form.setFields([{ name: [props.propertyKey, 'source'], value: imageName }])
		const values = form.getFieldsValue()
		if (values) props.onSectionChange({}, values)
	}

	const handleRemoveImage = () => {
		const form = props.formRef.current
		if (form === null) return

		form.setFields([{ name: [props.propertyKey, 'source'], value: null }])
		const values = form.getFieldsValue()
		if (values) props.onSectionChange({}, values)
	}

	return (
		<ImageContainerPanelStyled>
			<label>{txt('cms.source')}</label>
			<div className="image-source-container">
				<Form.Item name={[props.propertyKey, 'source']} noStyle>
					<Input disabled />
				</Form.Item>

				<Tooltip title={txt('global.delete')}>
					<Button danger type="primary" onClick={handleRemoveImage}>
						<FontAwesomeIcon icon={faTrash} />
					</Button>
				</Tooltip>
			</div>

			<Form.Item shouldUpdate noStyle>
				{() => {
					const form = props.formRef.current ?? null
					const imageName = form?.getFieldValue([props.propertyKey, 'source']) ?? null

					return (
						<div className="image-preview-container" onClick={() => setOpenImageCatalogue(true)}>
							{imageName && <img alt="preview" src={getImageSource(imageName)} />}
							{imageName === null && <Empty />}
						</div>
					)
				}}
			</Form.Item>

			<label>{txt('cms.alt')}</label>
			<Form.Item name={[props.propertyKey, 'alt']}>
				<Input required />
			</Form.Item>

			<div className="row-container">
				<div className="form-item-container">
					<label>{txt('cms.placement')}</label>
					<Form.Item name={[props.propertyKey, 'placement']}>
						<Radio.Group className="placement-icon-container">
							<Radio.Button value="left" className="placement-button">
								<div className="placement-icon-container">
									<FontAwesomeIcon icon={faImage} />
									<FontAwesomeIcon icon={faAlignCenter} />
								</div>
							</Radio.Button>

							<Radio.Button value="right" className="placement-button">
								<div className="placement-icon-container">
									<FontAwesomeIcon icon={faAlignCenter} />
									<FontAwesomeIcon icon={faImage} />
								</div>
							</Radio.Button>
						</Radio.Group>
					</Form.Item>
				</div>

				<div className="form-item-container">
					<label>{txt('cms.style')}</label>
					<Form.Item name={[props.propertyKey, 'style']}>
						<Radio.Group>
							<Radio.Button value="cover">
								<FontAwesomeIcon icon={faImage} />
							</Radio.Button>

							<Radio.Button value="float">
								<FontAwesomeIcon icon={faImages} />
							</Radio.Button>
						</Radio.Group>
					</Form.Item>
				</div>
			</div>

			<label>{txt('cms.link')}</label>
			<Form.Item name={[props.propertyKey, 'link']} initialValue="">
				<Input allowClear placeholder={txt('cms.link')} />
			</Form.Item>

			<AnimationCollapse
				propertyKey={props.propertyKey}
				formRef={props.formRef}
				onSectionChange={props.onSectionChange}
			/>

			<ImageCatalogue
				useVisible={[openImageCatalogue, setOpenImageCatalogue]}
				sourcePrefix="https://filer.boblberg.dk/cms/large_"
				onSelectImage={handleSelectImage}
				onQuery={CmsImageService.query}
				onCreate={CmsImageService.create}
				onRemove={CmsImageService.remove}
				showTransparent
			/>
		</ImageContainerPanelStyled>
	)
}

const ImageContainerPanelStyled = styled.div`
	.placement-button-container {
		display: flex;
		width: 100%;
	}

	.placement-button {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.placement-icon-container {
		gap: 1ch;
		display: flex;
	}

	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}

	.image-source-container {
		display: flex;
		gap: 0.65rem;
		padding-bottom: 0.65rem;
	}

	.image-preview-container {
		width: 100%;
		aspect-ratio: 2;
		background-color: #eee;
		border-radius: 1rem;
		overflow: hidden;
		display: flex;
		justify-content: center;
		cursor: pointer;
		border: 1px solid #eee;
		margin-bottom: 0.65rem;

		&:hover {
			border: 2px solid dodgerblue;
		}

		img {
			object-fit: contain;
		}

		.ant-empty {
			align-self: center;
		}
	}
`

export const ImagePanel = (props: TextPanelProps) => {
	const [openImageCatalogue, setOpenImageCatalogue] = useState(false)

	const handleSelectImage = (imageName: string) => {
		const form = props.formRef.current
		if (form === null) return

		form.setFields([{ name: [props.propertyKey, 'source'], value: imageName }])
		const values = form.getFieldsValue()
		if (values) props.onSectionChange({}, values)
	}

	const handleRemoveImage = () => {
		const form = props.formRef.current
		if (form === null) return

		form.setFields([{ name: [props.propertyKey, 'source'], value: '' }])
		const values = form.getFieldsValue()
		if (values) props.onSectionChange({}, values)
	}

	return (
		<ImagePanelStyled>
			<label>{txt('cms.source')}</label>
			<div className="image-source-container">
				<Form.Item name={[props.propertyKey, 'source']} noStyle>
					<Input disabled />
				</Form.Item>

				<Tooltip title={txt('global.delete')}>
					<Button danger type="primary" onClick={handleRemoveImage}>
						<FontAwesomeIcon icon={faTrash} />
					</Button>
				</Tooltip>
			</div>

			<Form.Item shouldUpdate noStyle>
				{() => {
					const form = props.formRef.current ?? null
					const imageName = form?.getFieldValue([props.propertyKey, 'source']) ?? null

					return (
						<div className="image-preview-container" onClick={() => setOpenImageCatalogue(true)}>
							{imageName && <img alt="preview" src={getImageSource(imageName)} />}
							{imageName === null && <Empty />}
						</div>
					)
				}}
			</Form.Item>

			<label>{txt('cms.alt')}</label>
			<Form.Item name={[props.propertyKey, 'alt']}>
				<Input required />
			</Form.Item>

			<div className="image-style-inputs">
				<div>
					<label>{txt('cms.shadow')}</label>
					<Form.Item name={[props.propertyKey, 'shadow']} initialValue={true} valuePropName="checked">
						<Switch />
					</Form.Item>
				</div>

				<div>
					<label>{txt('cms.fit')}</label>
					<Form.Item name={[props.propertyKey, 'fit']} initialValue="cover">
						<Select>
							<Select.Option value="cover">Cover</Select.Option>
							<Select.Option value="contain">Contain</Select.Option>
						</Select>
					</Form.Item>
				</div>
			</div>

			<label>
				<b>{txt('cms.text')}</b>
			</label>

			<div className="text-inputs">
				<div>
					<label>{txt('cms.text')}</label>
					<Form.Item name={[props.propertyKey, 'text']}>
						<Input placeholder="Aa" />
					</Form.Item>
				</div>

				<div>
					<label>{txt('cms.color')}</label>
					<Form.Item name={[props.propertyKey, 'textColor']}>
						<Input type="color" />
					</Form.Item>
				</div>
			</div>

			<label>{txt('cms.link')}</label>
			<Form.Item name={[props.propertyKey, 'link']} initialValue="">
				<Input allowClear placeholder={txt('cms.link')} />
			</Form.Item>

			<AnimationCollapse
				propertyKey={props.propertyKey}
				formRef={props.formRef}
				onSectionChange={props.onSectionChange}
			/>

			<ImageCatalogue
				useVisible={[openImageCatalogue, setOpenImageCatalogue]}
				sourcePrefix="https://filer.boblberg.dk/cms/large_"
				onSelectImage={handleSelectImage}
				onQuery={CmsImageService.query}
				onCreate={CmsImageService.create}
				onRemove={CmsImageService.remove}
			/>
		</ImagePanelStyled>
	)
}

const ImagePanelStyled = styled.div`
	.image-source-container {
		display: flex;
		gap: 0.65rem;
		padding-bottom: 0.65rem;
	}

	.image-preview-container {
		width: 100%;
		aspect-ratio: 2;
		background-color: #eee;
		border-radius: 1rem;
		overflow: hidden;
		display: flex;
		justify-content: center;
		cursor: pointer;
		border: 1px solid #eee;
		margin-bottom: 0.65rem;

		&:hover {
			border: 2px solid dodgerblue;
		}

		img {
			object-fit: contain;
		}

		.ant-empty {
			align-self: center;
		}
	}

	.image-style-inputs {
		display: flex;
		gap: 1rem;
		justify-content: space-evenly;
	}

	.text-inputs {
		display: flex;
		gap: 1rem;

		> * {
			flex: auto;
		}
	}
`
